/**
 * Zod schemas for vector annotation extension.
 */
import { z } from "zod"

const annotationPointSchema = z.object({
  x: z.number(),
  y: z.number()
})

const annotationStyleSchema = z.object({
  strokeColor: z.string(),
  strokeOpacity: z.number(),
  fillColor: z.string(),
  fillOpacity: z.number(),
  strokeWidth: z.number().optional()
})

const annotationViewportSchema = z.object({
  left: z.number(),
  top: z.number(),
  right: z.number(),
  bottom: z.number()
})

export const wsiViewerAnnotationSchema = z.object({
  id: z.string().min(1),
  imageName: z.string().min(1),
  points: z.array(annotationPointSchema).min(3),
  holes: z.array(z.array(annotationPointSchema)).optional(),
  style: annotationStyleSchema,
  label: z.string().optional(),
  group: z.string().optional(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
  bbox: annotationViewportSchema
})

const vectorLabelSchema = z.object({
  key: z.string(),
  label: z.string(),
  color: z.string()
})

const vectorGroupSchema = z.object({
  key: z.string(),
  name: z.string(),
  labels: z.array(vectorLabelSchema)
})

const editRegionDomainSchema = z.string().min(1).max(128)

export const vectorConfigSchema = z.object({
  type: z.literal("vector"),
  groups: z.array(vectorGroupSchema).optional(),
  labels: z.array(vectorLabelSchema).optional(),
  /**
   * Opt into ROI-restricted drawing: while the module is active, new vectors are
   * clipped to the slide's edit bounding boxes with this `domain` (plus
   * domain-less boxes). Unset = drawing is never restricted.
   */
  edit_region_domain: editRegionDomainSchema.optional()
})

/** Per-project overrides stored in project_modules.config. `null` turns edit regions off for the project. */
export const vectorProjectConfigSchema = z.strictObject({
  edit_region_domain: editRegionDomainSchema.nullable().optional()
})

export type VectorProjectConfig = z.infer<typeof vectorProjectConfigSchema>

/**
 * Effective edit-region domain: the per-project override when present (a
 * string, or null = explicitly off), else the template's. Invalid input is
 * treated as absent.
 */
export function resolveVectorEditRegionDomain(templateSchema: unknown, projectConfig: unknown): string | null {
  const overrides = vectorProjectConfigSchema.safeParse(projectConfig ?? {})
  if (overrides.success && overrides.data.edit_region_domain !== undefined) {
    return overrides.data.edit_region_domain
  }
  const template = vectorConfigSchema.safeParse(templateSchema)
  return template.success ? (template.data.edit_region_domain ?? null) : null
}

export const createVectorSchema = z.object({
  project_module_id: z.string().min(1),
  series_id: z.string().min(1),
  label_name: z.string().min(1).optional(),
  label_value: z.number().int().min(0).optional(),
  annotation: wsiViewerAnnotationSchema
})

export const updateVectorSchema = z.object({
  label_name: z.string().min(1).optional(),
  label_value: z.number().int().min(0).optional(),
  annotation: wsiViewerAnnotationSchema.optional()
})
